/**
 * Browser storage quota wrapper. Wraps `navigator.storage.estimate()`
 * with thresholds the UI uses to warn / block (D-027).
 *
 * Threshold semantics:
 *  - **ok**: < 80% used. Normal operation.
 *  - **warn**: 80–94% used. Sidebar surfaces a yellow notice.
 *  - **block**: ≥ 95% used. Upload disabled, cleanup prompted.
 *
 * Both thresholds are conservative — browsers can evict OPFS data
 * silently above ~95%, and we'd rather refuse a new upload than have
 * the active dataset disappear under the user's feet.
 */

export const WARN_THRESHOLD = 0.8;
export const BLOCK_THRESHOLD = 0.95;

export type StorageStatus = 'ok' | 'warn' | 'block' | 'unknown';

export type StorageEstimate = {
  /** Bytes used; null when the platform doesn't expose it. */
  usageBytes: number | null;
  /** Bytes total; null when the platform doesn't expose it. */
  quotaBytes: number | null;
  /** Fraction in [0, 1]; null when usage or quota are missing. */
  usageRatio: number | null;
  status: StorageStatus;
};

/**
 * Pure threshold classifier. Useful in tests and in any code path that
 * already has the raw numbers.
 */
export function classifyStorage(
  usage: number | null | undefined,
  quota: number | null | undefined,
): StorageEstimate {
  if (usage == null || quota == null || quota === 0) {
    return {
      usageBytes: usage ?? null,
      quotaBytes: quota ?? null,
      usageRatio: null,
      status: 'unknown',
    };
  }
  const ratio = usage / quota;
  let status: StorageStatus = 'ok';
  if (ratio >= BLOCK_THRESHOLD) status = 'block';
  else if (ratio >= WARN_THRESHOLD) status = 'warn';
  return {
    usageBytes: usage,
    quotaBytes: quota,
    usageRatio: ratio,
    status,
  };
}

/**
 * Read the current storage usage from the browser. Returns a `'unknown'`
 * status if the StorageManager API is missing (older browsers, some
 * privacy modes) — UI treats that the same as `'ok'` for upload gating.
 */
export async function readStorageEstimate(): Promise<StorageEstimate> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.estimate !== 'function'
  ) {
    return classifyStorage(null, null);
  }
  try {
    const raw = await navigator.storage.estimate();
    return classifyStorage(raw.usage ?? null, raw.quota ?? null);
  } catch {
    return classifyStorage(null, null);
  }
}

/** Human-readable byte formatter, e.g. `12.4 MB`. */
export function formatBytes(n: number | null): string {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
